import React from 'react';
import ReactDOM from 'react-dom/client';

// Dostupnostní guard přidává cache-buster jen pro první načtení po návratu UI.
// Hned ho ukliď, aby se nekopíroval do odkazů ani historie.
try {
  const loadedUrl = new URL(window.location.href);
  if (loadedUrl.searchParams.has('__acm_ui_recovered') || loadedUrl.searchParams.has('_acm')) {
    loadedUrl.searchParams.delete('__acm_ui_recovered');
    loadedUrl.searchParams.delete('_acm');
    window.history.replaceState(window.history.state, '', `${loadedUrl.pathname}${loadedUrl.search}${loadedUrl.hash}`);
  }
} catch { /* ignore */ }

// Chunk load error → reload stránky jednou.
// Nastane když má uživatel otevřenou starou verzi app a my deployujeme nový build.
// Staré hashe chunku neexistují → vite:preloadError / dynamic import fail → nový index.html.
const STALE_CHUNK_RE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \d+ failed/i;
function reloadOnceForStaleChunk() {
  const key = '__chunk_reload__';
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.location.reload();
  }
}
window.addEventListener('vite:preloadError', () => reloadOnceForStaleChunk());
window.addEventListener('unhandledrejection', (ev) => {
  const msg = String((ev.reason as { message?: string })?.message ?? ev.reason ?? '');
  if (STALE_CHUNK_RE.test(msg)) reloadOnceForStaleChunk();
});
// Po úspěšném loadu nového buildu smaž guard (další deploy zase smí reloadnout).
try {
  const build = document.querySelector('script[type="module"]')?.getAttribute('src') ?? '';
  const prev = sessionStorage.getItem('__chunk_build__');
  if (prev && prev !== build) sessionStorage.removeItem('__chunk_reload__');
  if (build) sessionStorage.setItem('__chunk_build__', build);
} catch { /* ignore */ }
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './lib/i18n'; // init i18n (side-effect) PŘED renderem App
import { initTheme } from './lib/theme';
import { initWeather } from './lib/weather';
import { initUiLayout } from './lib/ui-layout';
import {
  captureInstallPrompt,
  clearLegacyPwaDismiss,
  ensureStandaloneRecorded,
  registerServiceWorker,
} from './lib/pwa';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// Theme + layout před prvním paintem — bez FOUC (localStorage → data-theme / data-layout)
initTheme();
initWeather();
initUiLayout();
clearLegacyPwaDismiss();
captureInstallPrompt();
registerServiceWorker();
ensureStandaloneRecorded();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
        {/* Sonner <Toaster> se záměrně NErenderuje — žádné popupy vpravo
            nahoře (Filip 2026-07-24). toast() volání zachytává oznamovací
            centrum (NotificationBell) do persistentní historie. */}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
